import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, ConfirmModal, Field, ListPage, Modal, Select, Textarea } from "@redipos/ui";
import { finanzasApi, type SubscriptionRequestRow } from "../api.js";
import { REQUESTS_CHANGED_EVENT } from "../useRequestsBadge.js";

const TYPE_LABEL: Record<string, string> = {
  plan_change: "Cambio de plan",
  payment_notice: "Aviso de pago",
  module_interest: "Interés en módulos",
};

const STATUS_LABEL: Record<string, string> = {
  pending: "Pendiente",
  resolved: "Resuelta",
  rejected: "Descartada",
};

const STATUS_VARIANT: Record<string, "warning" | "success" | "neutral"> = {
  pending: "warning",
  resolved: "success",
  rejected: "neutral",
};

const METHOD_LABEL: Record<string, string> = {
  transfer: "Transferencia",
  cash: "Efectivo",
  card: "Tarjeta",
  other: "Otro",
};

function errMsg(e: unknown) {
  return e instanceof Error ? e.message : "Error";
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString("es-PY", { dateStyle: "short", timeStyle: "short" });
}

type Action =
  | { kind: "apply"; row: SubscriptionRequestRow }
  | { kind: "resolve"; row: SubscriptionRequestRow }
  | { kind: "reject"; row: SubscriptionRequestRow };

/**
 * Cola de pedidos que los clientes envían desde "Mi suscripción" (cambio de plan, aviso de pago).
 * El pedido también llega por el chat de soporte; acá se resuelve y queda registro.
 */
export default function RequestsPage() {
  const [scope, setScope] = useState<"pending" | "all">("pending");
  const [rows, setRows] = useState<SubscriptionRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [action, setAction] = useState<Action | null>(null);
  const [applyStatus, setApplyStatus] = useState("active");
  const [note, setNote] = useState("");
  const [working, setWorking] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    finanzasApi
      .requests(scope)
      .then((r) => {
        setRows(r);
        setErr(null);
      })
      .catch((e) => {
        setRows([]);
        setErr(errMsg(e));
      })
      .finally(() => setLoading(false));
  }, [scope]);

  useEffect(() => {
    load();
  }, [load]);

  const openAction = (a: Action) => {
    setAction(a);
    setNote("");
    setApplyStatus("active");
    setErr(null);
  };
  const closeAction = () => {
    if (!working) setAction(null);
  };

  const run = async () => {
    if (!action) return;
    setWorking(true);
    setErr(null);
    try {
      if (action.kind === "apply") {
        await finanzasApi.applyPlanRequest(action.row.id, { status: applyStatus, note });
      } else {
        await finanzasApi.closeRequest(action.row.id, {
          outcome: action.kind === "reject" ? "rejected" : "resolved",
          note,
        });
      }
      setAction(null);
      window.dispatchEvent(new Event(REQUESTS_CHANGED_EVENT));
      load();
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setWorking(false);
    }
  };

  const pendingCount = rows.filter((r) => r.status === "pending").length;
  const metrics = [
    { key: "pending", label: "Pendientes", value: loading ? "—" : pendingCount },
    {
      key: "plan",
      label: "Cambios de plan",
      value: loading ? "—" : rows.filter((r) => r.status === "pending" && r.type === "plan_change").length,
    },
    {
      key: "pay",
      label: "Avisos de pago",
      value: loading ? "—" : rows.filter((r) => r.status === "pending" && r.type === "payment_notice").length,
    },
  ];

  const detalle = (r: SubscriptionRequestRow) => {
    if (r.type === "plan_change") {
      return (
        <>
          <p className="text-fg">
            {r.fromPlanName ?? "Sin plan"} → <strong>{r.planName ?? "—"}</strong>
          </p>
          {r.note ? <p className="mt-0.5 text-xs text-fg-subtle">“{r.note}”</p> : null}
        </>
      );
    }
    if (r.type === "module_interest") {
      return <p className="text-fg">{r.note ?? "—"}</p>;
    }
    return (
      <>
        <p className="text-fg">
          {r.amount ? `${r.amount} · ` : ""}
          {METHOD_LABEL[r.method ?? ""] ?? r.method ?? "—"}
          {r.reference ? ` · ref. ${r.reference}` : ""}
        </p>
        {r.note ? <p className="mt-0.5 text-xs text-fg-subtle">“{r.note}”</p> : null}
      </>
    );
  };

  const accionTitulo =
    action?.kind === "apply"
      ? "Aplicar cambio de plan"
      : action?.kind === "reject"
        ? "Descartar solicitud"
        : "Marcar como resuelta";

  return (
    <>
      <ListPage
        title="Solicitudes"
        subtitle="Pedidos de cambio de plan y avisos de pago que envían los clientes"
        metrics={metrics}
        sectionTitle={scope === "pending" ? "Pendientes" : "Historial reciente"}
        actions={
          <div className="w-44">
            <Select
              aria-label="Filtrar solicitudes"
              value={scope}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setScope(e.target.value === "all" ? "all" : "pending")}
            >
              <option value="pending">Solo pendientes</option>
              <option value="all">Todas</option>
            </Select>
          </div>
        }
      >
        {err && !action ? (
          <p className="px-4 py-3 text-sm text-danger" role="alert">
            {err}
          </p>
        ) : null}
        <div className="table-x-scroll">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-surface-elevated text-left text-fg-subtle text-xs uppercase tracking-wide">
                <th className="px-4 py-3 font-medium">Cliente</th>
                <th className="px-4 py-3 font-medium">Pedido</th>
                <th className="px-4 py-3 font-medium">Detalle</th>
                <th className="px-4 py-3 font-medium">Recibida</th>
                <th className="px-4 py-3 font-medium">Estado</th>
                <th className="px-4 py-3 font-medium text-right"> </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-fg-subtle">
                    Cargando…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-fg-subtle">
                    {scope === "pending" ? "No hay solicitudes pendientes." : "Sin solicitudes."}
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} className="hover:bg-surface-elevated/60 transition-colors align-top">
                    <td className="px-4 py-3">
                      <p className="font-medium text-fg">{r.tenantName ?? r.tenantId}</p>
                      {r.requestedBy ? <p className="text-xs text-fg-subtle">{r.requestedBy}</p> : null}
                    </td>
                    <td className="px-4 py-3 text-fg-muted whitespace-nowrap">{TYPE_LABEL[r.type] ?? r.type}</td>
                    <td className="px-4 py-3">{detalle(r)}</td>
                    <td className="px-4 py-3 text-xs text-fg-subtle whitespace-nowrap">{formatDateTime(r.createdAt)}</td>
                    <td className="px-4 py-3">
                      <Badge variant={STATUS_VARIANT[r.status] ?? "neutral"}>{STATUS_LABEL[r.status] ?? r.status}</Badge>
                      {r.status !== "pending" && r.resolutionNote ? (
                        <p className="mt-1 max-w-56 text-xs text-fg-subtle">{r.resolutionNote}</p>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      {r.status === "pending" ? (
                        <div className="flex flex-wrap justify-end gap-2">
                          {r.type === "module_interest" ? (
                            <Link
                              to={`/clientes/${r.tenantId}`}
                              className="inline-flex min-h-9 items-center rounded-input border border-border-strong px-3 text-sm font-medium text-brand-500 hover:bg-surface-elevated"
                            >
                              Ver cliente
                            </Link>
                          ) : r.type === "plan_change" ? (
                            <Button type="button" onClick={() => openAction({ kind: "apply", row: r })}>
                              Aplicar plan
                            </Button>
                          ) : (
                            <Link
                              to={`/clientes/${r.tenantId}/facturacion`}
                              className="inline-flex min-h-9 items-center rounded-input border border-border-strong px-3 text-sm font-medium text-brand-500 hover:bg-surface-elevated"
                            >
                              Registrar pago
                            </Link>
                          )}
                          <Button type="button" variant="secondary" onClick={() => openAction({ kind: "resolve", row: r })}>
                            Marcar resuelta
                          </Button>
                          <Button type="button" variant="secondary" onClick={() => openAction({ kind: "reject", row: r })}>
                            Descartar
                          </Button>
                        </div>
                      ) : (
                        <div className="text-right">
                          <Link to={`/clientes/${r.tenantId}/facturacion`} className="text-sm font-medium text-brand-500 hover:text-brand-400">
                            Ver cliente
                          </Link>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </ListPage>

      {action?.kind === "apply" ? (
        <ConfirmModal
          open
          onClose={closeAction}
          onConfirm={run}
          loading={working}
          variant="primary"
          title={accionTitulo}
          confirmLabel="Aplicar plan"
          message={
            <span className="block space-y-3 text-left">
              <span className="block">
                Se cambiará el plan de <strong>{action.row.tenantName}</strong> a <strong>{action.row.planName}</strong> y se
                le avisará por el chat de soporte. El período de facturación arranca desde hoy.
              </span>
              <span className="block">
                <Field label="Estado de la suscripción">
                  <Select value={applyStatus} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setApplyStatus(e.target.value)}>
                    <option value="active">Activo (ya pagó / se registrará el pago)</option>
                    <option value="trial">Prueba (según los días del plan)</option>
                  </Select>
                </Field>
              </span>
              <span className="block">
                <Field label="Nota para el cliente (opcional)">
                  <Textarea value={note} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNote(e.target.value)} />
                </Field>
              </span>
              {err ? (
                <span className="block text-danger" role="alert">
                  {err}
                </span>
              ) : null}
            </span>
          }
        />
      ) : null}

      <Modal
        open={action?.kind === "resolve" || action?.kind === "reject"}
        onClose={closeAction}
        title={accionTitulo}
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={closeAction} disabled={working}>
              Cancelar
            </Button>
            <Button type="button" onClick={run} loading={working} loadingLabel="Guardando...">
              {action?.kind === "reject" ? "Descartar" : "Marcar resuelta"}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-fg-muted">
            {action?.kind === "reject"
              ? "La solicitud se cierra sin aplicar nada. El motivo se le envía al cliente por el chat de soporte."
              : "La solicitud se cierra sin aplicar cambios (por ejemplo, ya la gestionaste aparte). Podés dejar una nota para el cliente."}
          </p>
          <Field
            label={action?.kind === "reject" ? "Motivo" : "Nota para el cliente (opcional)"}
            required={action?.kind === "reject"}
          >
            <Textarea value={note} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNote(e.target.value)} />
          </Field>
          {err ? (
            <p className="text-sm text-danger" role="alert">
              {err}
            </p>
          ) : null}
        </div>
      </Modal>
    </>
  );
}
